import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useSocket } from '@/contexts/SocketContext';

type SyncStatus = 'idle' | 'syncing' | 'error';
interface SyncEvent {
  channelId: string;
  syncStatus: SyncStatus;
  trigger?: string;
  error?: string;
}

/**
 * Assina channel:sync-started/completed/error para ESTE canal. Retorna o status
 * ao vivo (para o botão Atualizar/badge) e invalida overview+metrics ao concluir.
 * String literal no socket.on: bug conhecido de Vite pre-bundle de SOCKET_EVENTS
 * (mesma convenção de useChannelsSocket).
 */
export const useChannelSyncSocket = (channelId: string | undefined): { liveStatus: SyncStatus | null } => {
  const { socket } = useSocket();
  const queryClient = useQueryClient();
  const [liveStatus, setLiveStatus] = useState<SyncStatus | null>(null);

  useEffect(() => {
    if (!socket || !channelId) return;
    const onStarted = (e: SyncEvent): void => {
      if (e.channelId === channelId) setLiveStatus('syncing');
    };
    const onDone = (e: SyncEvent): void => {
      if (e.channelId !== channelId) return;
      setLiveStatus('idle');
      void queryClient.invalidateQueries({ queryKey: ['channel-dashboard', channelId], exact: false });
    };
    const onError = (e: SyncEvent): void => {
      if (e.channelId !== channelId) return;
      setLiveStatus('error');
      void queryClient.invalidateQueries({ queryKey: ['channel-dashboard', channelId], exact: false });
    };
    socket.on('channel:sync-started', onStarted);
    socket.on('channel:sync-completed', onDone);
    socket.on('channel:sync-error', onError);
    return () => {
      socket.off('channel:sync-started', onStarted);
      socket.off('channel:sync-completed', onDone);
      socket.off('channel:sync-error', onError);
    };
  }, [socket, channelId, queryClient]);

  return { liveStatus };
};
